import React from "react";
import { Helmet } from "react-helmet";
import DashboardLayout from "../components/DashboardLayout";
import {
  Wand2,
  Zap,
  DollarSign,
  Clock,
  Shield,
  Server,
  FileText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import StatsCard from "@/components/StatsCard";
import FeatureCard from "@/components/FeatureCard";
import { NewsletterCallToAction } from "@/components/PageCallToAction";

const FineTuning = () => {
  return (
    <DashboardLayout>
      <Helmet>
        <title>Fine-Tuning Platform | OpenPipe</title>
        <meta
          name="description"
          content="Ship higher‑quality, faster LLMs that learn from your data with our developer‑first platform that makes continuous fine‑tuning as simple as logging requests."
        />
      </Helmet>
      <div className="max-w-6xl mx-auto px-4 space-y-24">
        {/* Hero Section */}
        <section className="space-y-6 pt-6 pb-8 text-center">
          <div className="inline-flex items-center justify-center p-2 bg-primary/10 text-primary rounded-full mx-auto">
            <Wand2 className="h-6 w-6" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight font-mono">
            Fine‑Tune Models Built for Production
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Ship higher‑quality, faster LLMs that learn from your data with our
            developer‑first platform that makes continuous fine‑tuning as simple
            as logging requests.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4 pt-4">
            <Button size="lg" className="primary-button" asChild external>
              <a
                href="https://app.openpipe.ai/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Get started
              </a>
            </Button>
            <Button
              variant="outline"
              size="lg"
              className="outline-button-hover flex items-center gap-2"
              asChild
            >
              <a
                href="https://docs.openpipe.ai/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FileText className="h-4 w-4" />
                Documentation
              </a>
            </Button>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="space-y-12">
          <h2 className="text-3xl font-bold text-center font-mono">
            Why OpenPipe?
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <StatsCard
              icon={Zap}
              title="90% Fewer Errors"
              value="90%"
              description="Production teams see error rates drop dramatically after swapping prompts for fine‑tuned models."
            />
            <StatsCard
              icon={DollarSign}
              title="8× Cheaper Than GPT‑4o"
              value="8×"
              description="Fine‑tuned Llama 3.1 models outperform GPT‑4o at a fraction of the cost."
            />
            <StatsCard
              icon={Clock}
              title="5 min to First Dataset"
              value="5 min"
              description="Automatic request logging turns real traffic into training data in minutes."
            />
          </div>
        </section>

        {/* How It Works Section */}
        <section className="space-y-12">
          <h2 className="text-3xl font-bold text-center font-mono">
            How It Works
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <FeatureCard
              icon={Server}
              title="Capture Data"
              description="OpenPipe records every LLM request/response so you never hunt for examples again."
            />
            <FeatureCard
              icon={Wand2}
              title="Train Models"
              description="Two clicks start a state‑of‑the‑art fine‑tune on your dataset."
            />
          </div>
        </section>

        {/* Security Section */}
        <section className="space-y-12">
          <h2 className="text-3xl font-bold text-center font-mono">
            Security & Compliance
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <FeatureCard
              icon={Shield}
              title="Enterprise Security"
              description="SOC 2 Type 2, HIPAA, and GDPR support keep sensitive data safe while you move fast."
            />
            <FeatureCard
              icon={Server}
              title="Flexible Deployment"
              description="Own your model weights and deploy anywhere — cloud, edge, or on‑prem."
            />
          </div>
        </section>

        {/* CTA Section */}
        <section className="text-center space-y-6 pb-12">
          <h2 className="text-3xl font-bold font-mono">
            Ready to Fine‑Tune the Right Way?
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Log your traffic, click "Train," and watch your app's quality
            improve while costs drop. Get started for free — or let us show you
            what a custom model can do for your stack.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4 pt-4">
            <Button size="lg" className="primary-button" asChild external>
              <a
                href="https://app.openpipe.ai/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Start Fine-Tuning Now
              </a>
            </Button>
            <Button
              variant="outline"
              size="lg"
              className="outline-button-hover flex items-center gap-2"
              asChild
            >
              <a
                href="https://docs.openpipe.ai/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FileText className="h-4 w-4" />
                Docs
              </a>
            </Button>
          </div>
        </section>

        <NewsletterCallToAction />
      </div>
    </DashboardLayout>
  );
};

export default FineTuning;
